import { Locale } from 'types';

/**
 * Every customer-facing string on the contract signing landing page. Legal reviews the wording, so copy
 * changes should only ever touch this file. Placeholders in braces are filled by the page.
 */
export const contractSigningTranslations = {
  [Locale.en]: {
    helmetTitle: 'Sign your contract | LightReach',
    greeting: 'Hi {firstName},',
    intro: 'Before you sign your LightReach contract, please complete a few quick steps.',
    loadError: 'We couldn’t load this page. Please try again in a moment.',
    linkNotFound:
      'This signing link is no longer active. Please contact your sales representative or LightReach support.',
    genericError: 'Something went wrong. Please try again.',
    continue: 'Continue',
    back: 'Back',

    languageHeading: 'Choose the language for your contract',
    languageHelp: 'This choice applies to everyone signing this contract.',
    languageEnglish: 'English',
    languageSpanish: 'Español',
    languageSpanishDetail: 'Your contract will include English and Spanish. You will sign the Spanish part.',
    languageOther: 'Other',
    languageOtherLabel: 'Which language?',
    languageRequired: 'Please choose a language.',
    languageOtherRequired: 'Please tell us which language.',
    languageTakenNotice: 'The other signer just chose the contract language. Please review it below.',

    languageSetHeadingEnglish: 'Your contract will be in English',
    languageSetHeadingSpanish: 'Your contract will be in Spanish',
    languageSetBody:
      'The other signer on this contract already chose this language. If you disagree, contact LightReach support or your sales representative.',
    languagePreferenceLabel: 'Which language would you prefer for future communication?',

    notOfferedHeading: 'We don’t offer signing in {language} yet',
    notOfferedBody:
      'We’ve noted your preference. Please contact LightReach support so we can help you, or choose a different language.',
    notOfferedChooseAnother: 'Choose a different language',

    englishOnlyHeading: 'Your contract is in English only',
    englishOnlyBody:
      'This contract is not yet available in Spanish. Inside the contract, you’ll be asked to confirm that you understand it or that it was translated for you.',
    englishOnlyAcknowledge: 'I understand',

    keyTermsHeading: 'Your key terms',
    keyTermsYearOnePayment: 'First-year monthly payment',
    keyTermsEscalator: 'Annual payment increase',
    keyTermsTerm: 'Agreement term',
    keyTermsYears: '{years} years',
    keyTermsHelp: 'Your contract contains all terms and conditions.',
    keyTermsAcknowledge: 'These match what I expected',

    consentHeading: 'Consent to sign electronically',
    consentReadFull: 'Read full disclosure',
    consentClose: 'Close',
    consentRequired: 'Please check the box to continue.',
    consentChanged: 'The consent text was updated. Please review it again.',

    reviewHeading: 'You’re ready to sign',
    reviewAndSign: 'Review and sign',
    waitingHeading: 'Preparing your contract…',
    waitingBody: 'This usually takes less than a minute. Please keep this page open.',
    unavailableBody: 'We couldn’t open your contract right now. Please contact LightReach support.',

    coachingHeading: 'The next screen may be in English',
    coachingBody: 'Here are the words you’ll need:',
    coachingAccept: 'Accept',
    coachingSign: 'Sign',
    coachingFinish: 'Finish',

    contactPhone: '(800) 597-8527',
    contactLabel: 'LightReach support:',
  },
  [Locale.es]: {
    helmetTitle: 'Firme su contrato | LightReach',
    greeting: 'Hola {firstName}:',
    intro: 'Antes de firmar su contrato de LightReach, complete unos pasos rápidos.',
    loadError: 'No pudimos cargar esta página. Inténtelo de nuevo en un momento.',
    linkNotFound:
      'Este enlace de firma ya no está activo. Comuníquese con su representante de ventas o con atención al cliente de LightReach.',
    genericError: 'Algo salió mal. Inténtelo de nuevo.',
    continue: 'Continuar',
    back: 'Atrás',

    languageHeading: 'Elija el idioma de su contrato',
    languageHelp: 'Esta elección aplica a todas las personas que firman este contrato.',
    languageEnglish: 'English',
    languageSpanish: 'Español',
    languageSpanishDetail: 'Su contrato incluirá inglés y español. Usted firmará la parte en español.',
    languageOther: 'Otro',
    languageOtherLabel: '¿Qué idioma?',
    languageRequired: 'Elija un idioma.',
    languageOtherRequired: 'Indíquenos qué idioma.',
    languageTakenNotice: 'El otro firmante acaba de elegir el idioma del contrato. Revíselo a continuación.',

    languageSetHeadingEnglish: 'Su contrato estará en inglés',
    languageSetHeadingSpanish: 'Su contrato estará en español',
    languageSetBody:
      'El otro firmante de este contrato ya eligió este idioma. Si no está de acuerdo, comuníquese con atención al cliente de LightReach o con su representante de ventas.',
    languagePreferenceLabel: '¿Qué idioma prefiere para comunicaciones futuras?',

    notOfferedHeading: 'Todavía no ofrecemos la firma en {language}',
    notOfferedBody:
      'Hemos registrado su preferencia. Comuníquese con atención al cliente de LightReach para ayudarle, o elija otro idioma.',
    notOfferedChooseAnother: 'Elegir otro idioma',

    englishOnlyHeading: 'Su contrato está solo en inglés',
    englishOnlyBody:
      'Este contrato todavía no está disponible en español. Dentro del contrato, se le pedirá confirmar que lo entiende o que se lo tradujeron.',
    englishOnlyAcknowledge: 'Entiendo',

    keyTermsHeading: 'Sus términos clave',
    keyTermsYearOnePayment: 'Pago mensual del primer año',
    keyTermsEscalator: 'Aumento anual del pago',
    keyTermsTerm: 'Plazo del contrato',
    keyTermsYears: '{years} años',
    keyTermsHelp: 'Su contrato contiene todos los términos y condiciones.',
    keyTermsAcknowledge: 'Coinciden con lo que esperaba',

    consentHeading: 'Consentimiento para firmar electrónicamente',
    consentReadFull: 'Leer la divulgación completa',
    consentClose: 'Cerrar',
    consentRequired: 'Marque la casilla para continuar.',
    consentChanged: 'El texto del consentimiento cambió. Revíselo de nuevo.',

    reviewHeading: 'Está listo para firmar',
    reviewAndSign: 'Revisar y firmar',
    waitingHeading: 'Preparando su contrato…',
    waitingBody: 'Normalmente toma menos de un minuto. Mantenga esta página abierta.',
    unavailableBody:
      'No pudimos abrir su contrato en este momento. Comuníquese con atención al cliente de LightReach.',

    coachingHeading: 'Es posible que la siguiente pantalla esté en inglés',
    coachingBody: 'Estas son las palabras que necesitará:',
    coachingAccept: 'Aceptar',
    coachingSign: 'Firmar',
    coachingFinish: 'Terminar',

    contactPhone: '(800) 597-8527',
    contactLabel: 'Atención al cliente de LightReach:',
  },
} satisfies Record<Locale, Record<string, string>>;

export type ContractSigningCopyKey = keyof (typeof contractSigningTranslations)[Locale.en];
